import { type Store, createLens } from '@frontend/viewer-core';

import type { SnapResolver } from './snapResolver';
import {
  EMPTY_MEASURE,
  type DxfSessionState,
  type MeasureManager,
  type MeasureState,
} from './types';

export const createMeasureSlice = (
  state: Store<DxfSessionState>,
): Store<MeasureState> =>
  createLens(
    state,
    (current) => current.measure,
    (current, measure) => ({ ...current, measure }),
  );

type Dependencies = {
  measure: Store<MeasureState>;
  resolve: SnapResolver;
};

export const createMeasureController = ({
  measure,
  resolve,
}: Dependencies): MeasureManager => ({
  getState: measure.getState,
  subscribe: measure.subscribe,
  commitPoint(screenPoint) {
    const world = resolve(screenPoint).world;
    if (!world) {
      return;
    }
    measure.setState((previous) => {
      // A third click starts over rather than extending: a measurement is
      // between two points, and the previous one is cleared as the new one begins.
      const isStartingOver = previous.from === null || previous.to !== null;
      return isStartingOver
        ? { from: world, to: null }
        : { from: previous.from, to: world };
    });
  },

  clear() {
    measure.setState((previous) =>
      previous.from === null && previous.to === null ? previous : EMPTY_MEASURE,
    );
  },
});
